/**
  * @class msysDomainClient
  * @description
  * Implements communication client that connects to server implemented in msysServer
  * through WebSockets.
  * msysDomainClient is vital component for running client Mosy applications.
  * Firstly, it checks if it already has app_id, private key and still valid app certificate.
  * If not, application shall not work and should allow only enrollment resp. waiting for connection to server.
  */

import msysAbstractClient from 'msysAbstractClient'
const mfs = require('msysFS')
const mcrypt = require('msysCrypt')

const ms = require('msysMessage/binary')

export default class msysDomainClient extends msysAbstractClient {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger'], dependencies)
    cfg_schema = _.extend({
      offlines_dir: { description: 'path where offline messages are stored', type: 'string', default: 'offlines' },
      offlines_ext: { description: 'extension for offline message files', type: 'string', default: 'mm' },
      offlines_batchsize: { description: 'max number of messages sent to server in one batch', type: 'integer', default: 2 },
      offlines_cooldown: { description: 'cooldown time between two offline batches in milliseconds', type: 'integer', default: 100 },
      enrollment_server: { description: 'default enrollment server if none is provided in enrollment_server file', type: 'string' },
      enrollment_note: { description: 'default enrollment note if none is provided in enrollment_note file', type: 'string', default: MOSY_APP+MOSY_HOST+' $HOSTNAME $IP' },
      offline_message_clean_interval: { description: 'how often clean expired offline messages in milliseconds', type: 'integer', default: 60000 },
      identity: {
        description: 'identity configuration',
        type: 'object',
        properties: {
          app_id: { description: 'application ID', type: 'string' },
          app_key_password: { description: 'application ID', type: 'string' },
          app_privatekey: { description: 'application ID', type: 'string' },
          app_publickey: { description: 'application ID', type: 'string' },
          app_certificate: { description: 'application ID', type: 'string' },
        },
        required: ['app_id', 'app_publickey', 'app_privatekey', 'app_key_password'],
      },
      device_identification: {
        description: 'configures way of determining device_id',
        type: 'string',
        enum: ['detect', 'enrollment_note'],
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this.action_connected = 'mmServerConnected'
    this.action_disconnected = 'mmServerDisconnected'
    this.offline_messages = {}
    this.peering_appid = ''
    if(!MOSY_PRODUCTION && typeof (window)!=='undefined') window.offlines = this.offline_messages
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  async mmStart(sender, params) {
    // setup daemon for clearing obsolete offline messages
    if(mfs.platform()!=='web') { setTimeout(()=>this.startDaemon(this.config.offline_message_clean_interval, 'deleteExpiredOffllineMessages', true), 3000) }

    super.mmStart(sender, params)
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  mmStartedAll(sender, params) {
    // setTimeout to prevent warning on long action of mmStartedAll
    setTimeout(()=>{
      this.loadAppIdentityVariables()
      this.identifyOffline()
      this.reconnectServer(false, false) // immediately connect to last used server
    }, 1)
  } // mmStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Loads and verifies application identity variable
  */
  loadAppIdentityVariables() {
    this.debug('CDBG loadAppIdentityVariables START')

    let dont_save, dont_save_cert, app_id, app_pub, app_key, app_key_pass, app_cert, unit_id
    if(this.config.identity) {
      // load from configuration
      dont_save = true // do not save hard configuration values to files
      app_id = this.config.identity.app_id
      app_pub = this.config.identity.app_publickey
      app_key = this.config.identity.app_privatekey
      app_key_pass = this.config.identity.app_key_password
      unit_id = this.config.identity.unit_id
      this.config.app_key_password = app_key_pass
      app_cert = this.config.identity.app_certificate
      dont_save_cert = !!app_cert // do not save hard configuration certificate to file
    }else{
      // read from local store
      dont_save = false
      dont_save_cert = false
      app_id = mfs.getItem('app_id')
      app_id = typeof (app_id)==='string' ? app_id.trim() : ''
      app_pub = mfs.getItem('app_publickey')
      app_key = mfs.getItem('app_privatekey')
      app_key_pass = this.config.app_key_password
      app_cert = mfs.getItem('app_certificate') || ''
      unit_id = mfs.getItem('unit_id') || ''
    }

    const app_id0 = app_id
    const app_pub0 = app_pub
    const app_key0 = app_key
    const app_cert0 = app_cert

    this.debug('CDBG loadAppIdentityVariables LOADED: app_id='+app_id+' app_pub='+!!app_pub+' app_key='+!!app_key+' app_key_pass='+!!app_key_pass+' app_cert='+!!app_cert+' unit_id='+unit_id)

    // clean everything if app_id not set
    if(!app_id || !/^[0-9a-zA-Z]{6,}$/.test(app_id)) {
      this.error('loadAppIdentityVariables INVALID APP_ID: '+app_id)
      app_id = ''
      app_cert = ''
      unit_id = ''
    }

    // check if public/private key match
    if(!mcrypt.verifyKeyPair(app_pub, app_key, app_key_pass)) {
      this.debug('CDBG loadAppIdentityVariables: mismatched public/private keys')
      if(app_pub) this.error('loadAppIdentityVariables INVALID PUBLIC/PRIVATE KEY PAIR !!!');
      [app_pub, app_key] = mcrypt.generateKeyPair(app_key_pass)
      app_cert = ''
    }

    // remove invalid (expired, invalid or missing) app certificate
    if(!app_id || !app_cert || !mcrypt.verifyMyCertificate(app_cert, app_key, app_key_pass)) {
      this.debug('CDBG loadAppIdentityVariables: cannot verify certificate')
      if(app_id && app_cert) this.error('loadAppIdentityVariables INVALID CERTIFICATE !!!')
      app_cert = ''
    }

    this.debug('CDBG loadAppIdentityVariables VERIFIED: app_id='+app_id+' app_pub='+!!app_pub+' app_key='+!!app_key+' app_key_pass='+!!app_key_pass+' app_cert='+!!app_cert)

    // save correct results to local store
    if(app_id !== app_id0 && !dont_save) {
      this.error('loadAppIdentityVariables CHANGED app_id: '+app_id0+' > '+app_id)
      mfs.setItem('app_id', app_id)
    }
    if(app_pub !== app_pub0 && !dont_save) {
      this.error('loadAppIdentityVariables CHANGED app_publickey: '+app_pub0+' > '+app_pub)
      mfs.setItem('app_publickey', app_pub)
    }
    if(app_key !== app_key0 && !dont_save) {
      this.error('loadAppIdentityVariables CHANGED app_privatekey')
      mfs.setItem('app_privatekey', app_key)
    }
    if(app_cert !== app_cert0 && !dont_save_cert) {
      this.warn('loadAppIdentityVariables CHANGED app_certificate: '+app_cert0+' > '+app_cert)
      mfs.setItem('app_certificate', app_cert)
    }

    // put it in this for easy reference
    this.app_id = app_id
    this.apppub = app_pub
    this.app_key = app_key
    this.app_cert = app_cert
    this.unit_id = unit_id
    this.debug('CDBG loadAppIdentityVariables SAVED: app_id='+app_id+' app_pub='+!!app_pub+' app_key='+!!app_key+' app_key_pass='+!!app_key_pass+' app_cert='+!!app_cert)
  } // loadAppIdentityVariables

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Tries to identify when server connection is not available using stored app_id and certificate
  */
  identifyOffline() {
    // we tested our own certificate against our own certificate
    // this validates the certificate is technically correct and not expired
    if(this.app_id && this.app_cert) {
      this.info('mmAppIdentified offline: '+this.app_id, this.unit_id ? ' unit_id: '+this.unit_id : '')
      this.publish('#ALL', 'mmAppIdentified', {
        app_id: this.app_id,
        app_key: this.app_key,
        app_cert: this.app_cert,
        unit_id: this.unit_id,
      })
    }else{
      this.warn('mmAppIdentified offline: cannot identify offline: app_id='+this.app_id+' app_cert='+!!this.app_cert+' servers='+JSON.stringify(this.config.servers))
      this.publish('#ALL', 'mmAppIdentified')
      if(this.config.servers.length===0) { this.enrollNeeded() }
    }
  } // identifyOffline

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Incoming message from domain server
  * @param {object} sender - sender
  * @param {object} target - target
  * @param {string} action - message action
  * @param {object} params - message params
  */
  mxMessage(sender, target, action, params) {
    // we expect messages purely from server, i.e. ":" sender
    this.debug('CDBG mxMessage: '+JSON.stringify(sender)+'>'+JSON.stringify(target)+'.'+action)
    sender.scope = ':'
    // sender.app_id = '';
    this.publishAs(sender, target, action, params)
  } // mxMessage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Send a message to server through msysDomainClient
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {object} params.target - source or target address
  * @param {string} params.action - message type
  * @param {object} [params.params] - parameters of the message
  */
  mmSendToServer(sender, params) {
    const ttl = _.natural(params?.params?._ttl)
    if(params.params) delete params.params._ttl
    this.debug(`CDBG mmSendToServer ${ttl>0?'with TTL '+ttl:'without TTL'}: `+JSON.stringify(sender)+'>'+JSON.stringify(params.target)+'.'+params.action)

    const msg = {
      sender: _.omit(sender, ['scope', 'app_id']), // we send just listener_id+request_id, as server knows our app_id through mxIdentify
      target: params.target, // we send just listener_id+request_id, as there is only 1 logical server at the other side
      action: params.action,
      params: params.params,
    }

    const success = this.ws?.app_id && this.sendToServer(msg)

    if(!success) {
      if(ttl>0) {
        // this is message with TTL; if send failed, store the message in offline message queue
        this.debug('CDBG mmSendToServer with TTL '+ttl+' failed; sending offline')
        this.saveOfflineMessage(msg, ttl)
      }else{
        // this is message without TTL; if send failed, immediately send mmUndelivered to the sender
        if(sender.request_id) { this.publishAs(_.omit(sender, ['request_id']), { listener_id: '#ME', request_id: sender.request_id }, 'mmUndelivered', { error: 'server_disconnected', target: this.address2string(sender) }) }
      }
    }
  } // mmSendToServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * saves message to be sent to server when online
  */
  saveOfflineMessage(msg, ttl) {
    this.debug('CDBG saveOfflineMessage')
    const order_id = this.oid()
    if(!order_id) return
    const expiration = parseInt(Date.now()/1000) + _.natural(ttl)
    const msg_id = order_id+'_'+expiration

    // add _sent time stamp
    msg.params = msg.params || {}
    msg.params._sent = unow()

    if(!msg.sender || !msg.target || !msg.action) {
      this.error('Invalid message for offline: '+JSON.stringify(msg))
      return
    }

    const binary = ms.msg2uint(msg, true)
    if(mfs.platform()==='web') {
      // for web we only store it in memory
      this.offline_messages[msg_id] = binary
    }else{
      // store it as file
      const file_path = this.config.offlines_dir+'/'+msg_id+'.'+this.config.offlines_ext
      mfs.writeFile(file_path, binary, 'binary', (err)=>{
        if(err) {
          this.error('Failed to save offline message to file: '+file_path)
        }
      })
    }
  } // saveOfflineMessage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * sends stored offline messages if server is connected
  */
  async sendOfflineMessages() {
    if(this.sendingOfflineMessages) return // another "instance" of this function is running
    if(!this.ws?.app_id) return // we are not connected
    this.sendingOfflineMessages = true
    this.debug('CDBG sendOfflineMessages start')

    const batch_size = this.config.offlines_batchsize
    const batch_cooldown = this.config.offlines_cooldown || 1

    while(true) {
      const ids = await this.getOfflineMessageIds(batch_size)
      if(ids.length === 0 || !this.ws?.app_id) break
      for(const id of ids) {
        if(!this.ws?.app_id) return // we've lost connection
        const msg = await this.getOfflineMessage(id)
        if(msg) {
          this.debug('Sending offline message: '+id+': '+msg.action)
          const success = this.sendToServer(msg)
          if(!success) {
            this.error(`Failed to send offline message ${id}`)
          }
        }
        await this.deleteOfflineMessage(id)
      }
      await asyncTimeout(batch_cooldown)
    }

    this.debug('CDBG sendOfflineMessages end')
    this.sendingOfflineMessages = false
  } // sendOfflineMessages

  async getOfflineMessageIds(batch_size) {
    if(mfs.platform()==='web') {
      return await this.getOfflineMessageIdsMemory(batch_size)
    }else {
      return await this.getOfflineMessageIdsFile(batch_size)
    }
  } // getOfflineMessageIds

  async getOfflineMessageIdsMemory(batch_size) {
    let offlines = this.offline_messages
    if(!offlines || typeof offlines !== 'object') {
      offlines = this.offline_messages = {}
    }

    let ids = Object.keys(offlines).sort((a, b)=>{
      a = a.replace(/_.*/, '')
      b = b.replace(/_.*/, '')
      return(a<b ? -1 : a>b ? 1 : 0)
    })

    if(batch_size > 0) ids = ids.slice(0, batch_size)
    return ids
  } // getOfflineMessageIdsMemory

  async getOfflineMessageIdsFile(batch_size) {
    const start = this.config.offlines_dir.length + 1
    const end = this.config.offlines_ext.length + 1
    return new Promise((resolve)=>{
      mfs.find(this.config.offlines_dir, {
        type: 'file',
        maxdepth: 1,
        include: '[\\/]\\w+_\\d+\\.'+this.config.offlines_ext+'$',
        finish: entries=>{
          let ids = entries.map(entry=>entry[0].substring(start, entry[0].length - end))

          ids = ids.sort((a, b)=>{
            a = a.replace(/_.*/, '')
            b = b.replace(/_.*/, '')
            return(a<b ? -1 : a>b ? 1 : 0)
          })

          if(batch_size > 0) ids = ids.slice(0, batch_size)
          resolve(ids)
        },
      })
    })
  } // getOfflineMessageIdsFile

  async getOfflineMessage(id) {
    // retrieve offline message as a string from memory or file
    let msg
    if(mfs.platform()==='web') {
      msg = this.offline_messages?.[id]
    }else {
      const file = this.config.offlines_dir+'/'+id+'.'+this.config.offlines_ext
      try{
        const binary = await mfs.promises.readFile(file, 'binary')
        msg = ms.uint2msg(binary, true)
      }catch(e) {
        this.error('Failed to read offline message: '+id)
        return
      }
    }

    // verify message structure
    if(!msg || typeof msg !== 'object') {
      this.error(`Invalid offline message ${id}`)
      return
    }

    const { sender, target, action } = msg

    if(typeof sender?.listener_id !== 'string' || sender?.listener_id==='') {
      this.error(`Invalid offline message sender ${id}: `+msg.sender)
      return
    }

    if(typeof target?.listener_id !== 'string' || sender?.listener_id==='') {
      this.error(`Invalid offline message target ${id}: `+msg.target)
      return
    }

    if(typeof action !== 'string' || action==='') {
      this.error(`Invalid offline message action ${id}: `+action)
      return
    }

    return msg
  } // getOfflineMessage

  async deleteOfflineMessage(id) {
    if(mfs.platform()==='web') {
      const exists = !!this.offline_messages?.[id]
      if(this.offline_messages && typeof this.offline_messages === 'object') {
        delete this.offline_messages[id]
      }
      return exists
    }else {
      const file = this.config.offlines_dir+'/'+id+'.'+this.config.offlines_ext
      try{
        await mfs.promises.delete(file)
        return true
      }catch(e) {
        this.error('Failed to delete offline message: '+id+': '+file)
        return false
      }
    }
  } // deleteOfflineMessage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * saves message to be sent to server when online
  */
  async deleteExpiredOffllineMessages() {
    this.debug('CDBG deleteExpiredOffllineMessages start')
    const current_seconds = parseInt(Date.now()/1000)

    const all_ids = await this.getOfflineMessageIds()
    const expired_ids = all_ids.filter(id=>{
      const expiry = parseInt(id.replace(/.*_/, ''))
      return expiry>0 && expiry<current_seconds
    })

    let deleted = 0
    for(const id of expired_ids) {
      if(await this.deleteOfflineMessage(id)) deleted++
    }

    if(deleted>0) this.debug(`Deleted ${deleted} expired offline messages`)
    this.debug('CDBG deleteExpiredOffllineMessages end')
  } // deleteExpiredOffllineMessages

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Invoke enrollment if enrollment data configured, otherwise inform all listeners that enrollment is needed
  */
  enrollNeeded() {
    this.error('enrollNeeded DELETING app_certificate')
    mfs.setItem('app_certificate')
    const enrollment_server = _.toString(mfs.getItem('enrollment_server') || this.config.enrollment_server).replace(/\r?\n.*/, '')
    const enrollment_note =
      (_.toString(mfs.getItem('enrollment_note')) || this.config.enrollment_note)
        .replace(/\r?\n.*/, '')
        .replace(/\$HOSTNAME\b/g, mfs.hostname())
        .replace(/\$IP\b/g, _.toString(this?.ws?.my_ip_from_server))

    this.debug('CDBG enrollNeeded: enrollment_server='+enrollment_server+' enrollment_note='+enrollment_note+'  getItem='+mfs.getItem('enrollment_note')+'  this.config.enrollment_note='+this.config.enrollment_note)

    if(!enrollment_server || !enrollment_note) {
      this.publish('#ALL', 'mmAppEnrollNeeded')
    }else if(/CONFIGURE_ME/i.test(enrollment_note)) {
      this.error('enrollNeeded: enrollment_note is not configured')
      this.publish('#ALL', 'mmExit')
      setTimeout(()=>process.exit(1), 1000)
    }else if(/CONFIGURE_ME/i.test(enrollment_server)) {
      this.error('enrollNeeded: enrollment_server is not configured')
      this.publish('#ALL', 'mmExit')
      setTimeout(()=>process.exit(1), 1000)
    }else if(!/^wss?:\/\/.+/i.test(enrollment_server)) {
      this.error('enrollNeeded: enrollment_server is invalid: '+enrollment_server)
      this.publish('#ALL', 'mmExit')
      setTimeout(()=>process.exit(1), 1000)
    }else{
      this.publish('#ME', 'mmAppEnroll', {
        server: enrollment_server,
        note: enrollment_note,
      })
    }
  } // enrollNeeded

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server requests client application to authenticate itself
  * @param {string} params.challenge - random challenge string to be returned signed
  */
  mxChallenge(params) {
    this.debug('CDBG mxChallenge')
    this.client_challenge = mcrypt.challenge()
    if(this.ws) {
      this.ws.app_protocol = params.protocol
      this.ws.ident = params.server
      this.ws.my_ip_from_server = _.toString(params.clientip)
    }
    const challenge = params.challenge + this.client_challenge
    const signature = mcrypt.sign(mcrypt.privateFromPEM(this.app_key, this.config.app_key_password), challenge)
    this.debug('CDBG mxChallenge: signature='+signature)

    if(this.enroll) {
      // enrollment parameters prepared, so append signature and send request to enroll
      this.debug('CDBG mxChallenge: this.enroll='+JSON.stringify(this.enroll))
      _.extend(this.enroll, {
        client: MOSY_APP_WITH_VERSION,
        protocol: this.PROTOCOL_VERSION,
        time: Date.now(),
        timezone: new Date().getTimezoneOffset(),
        challenge: this.client_challenge,
        signature,
      })
      this.info('mxChallenge: mxEnroll('+JSON.stringify(this.enroll)+')')
      this.sendToServer({
        action: 'mxEnroll',
        params: this.enroll,
      })
      this.debug('CDBG mxChallenge: mxEnroll='+JSON.stringify(this.enroll))
      this.enroll = null
    }else{
      // we want to identify
      if(signature && this.app_id) {
        const identify_json = {
          client: MOSY_APP_WITH_VERSION,
          protocol: this.PROTOCOL_VERSION,
          time: Date.now(),
          timezone: new Date().getTimezoneOffset(),
          app_id: this.app_id,
          challenge: this.client_challenge,
          signature,
        }
        const identify_debug = { ...identify_json }
        identify_debug.challenge = '...'
        identify_debug.signature = '...'
        this.debug('mxChallenge: mxIdentify('+JSON.stringify(identify_debug)+')')
        this.sendToServer({
          action: 'mxIdentify',
          params: identify_json,
        })
      }else{
        this.debug('CDBG mxChallenge: this.enrollNeeded()')
        this.enrollNeeded()
      }
    }
  } // mxChallenge

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server don't know provided app_id, we have to enroll
  * @param {string} params.reason - reason for error
  */
  mxError(params) {
    this.error('mxError: '+JSON.stringify(params))
    if(/invalid app_id/.test(params.reason)) {
      this.debug('CDBG mxChallenge: this.enrollNeeded()')
      mfs.setItem('app_id')
      this.enrollNeeded()
      super.mxError(params)
    }else if(/unknown app_id|invalid public key|invalid signature|identify error/.test(params.reason)) {
      this.enrollNeeded()
      super.mxError(params)
    }else{
      super.mxError(params)
    }
  } // mxError

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server requests client application to autenticate itself
  * @param {string} challenge - random challenge string to be returned signed
  */
  mxEnrolling(params) {
    const app_id0 = mfs.getItem('app_id')
    const app_id = params.app_id
    if(app_id !== app_id0) {
      this.error('mxEnrolling CHANGED app_id: '+app_id0+' > '+app_id)
      mfs.setItem('app_id', app_id)
    }
    this.error('mxEnrolling DELETING app_certificate')
    mfs.setItem('app_certificate')
    this.loadAppIdentityVariables()
    this.publish('#ALL', 'mmAppEnrolling', { app_id: params.app_id })
  } // mxEnrolling

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server authorizes the application
  * @param {string} params.note - note for the application
  * @param {string} params.cert - fresh application certificate
  * @param {string} params.cfg - config for the application
  */
  mxIdentified(params) {
    // stop timeouter
    if(this.timeouter) {
      clearTimeout(this.timeouter)
      this.timeouter=null
    }

    const app_cert = _.toString(params.cert)
    const app_id = mcrypt.verifyMyCertificate(app_cert, this.app_key, this.config.app_key_password)
    if(!app_id) {
      this.error('mxIdentified INVALID CERTIFICATE FROM SERVER !!!')
      this.reconnectServer(true, false, 'invalid certificate from server')
      return
    }

    const app_id0 = mfs.getItem('app_id')
    if(app_id !== app_id0) {
      this.warn('mxIdentified CHANGED app_id: '+app_id0+' > '+app_id)
      mfs.setItem('app_id', app_id)
    }

    const app_cert0 = mfs.getItem('app_certificate') || ''
    if(app_cert !== app_cert0) {
      this.debug('mxIdentified CHANGED app_certificate: '+app_cert0.substr(28, 20).replace(/\n/g, '\\n')+' > '+app_cert.substr(28, 20).replace(/\n/g, '\\n'))
      mfs.setItem('app_certificate', app_cert)
    }

    const app_note0 = mfs.getItem('enrollment_note')
    const app_note = _.toString(params.note)
    if(app_note!==app_note0) {
      if(app_note.length>0) {
        this.error('mxIdentified CHANGED note: '+app_note0+' > '+app_note)
        mfs.setItem('enrollment_note', app_note)
      }else{
        this.error('mxIdentified EMPTY note FROM SERVER !!!')
      }
    }

    this.loadAppIdentityVariables()

    const old_server_cfg = mfs.getJSON('server.cfg')
    let new_server_cfg_txt = JSON.stringify(params.cfg)
    const url = /^(wss?:\/\/)([^:/]+)(?::(\d+))?/.exec(this.ws ? this.ws.url : '')
    if(url) {
      const proto = url[1]; const ip = url[2]; const port = (url[3]>0 && url[3]!=='80') ? ':'+url[3] : ''
      new_server_cfg_txt = new_server_cfg_txt
        .replace(/\$SERVER_URL/g, proto+ip+port)
        .replace(/\$SERVER_PROTO/g, proto)
        .replace(/\$SERVER_IP/g, ip)
        .replace(/\$SERVER_PORT/g, port)
    }
    try{ params.cfg = JSON.parse(new_server_cfg_txt) }catch(e) {}
    if(JSON.stringify(old_server_cfg) !== new_server_cfg_txt) {
      // configuration has changed, will be in effect after restart
      try{
        if(!params.cfg.msysDomainClient.servers[0].match(/^wss?:\/\/.+/)) throw new Error('INVALID SERVER IN CONFIGURATION')
        this.info('mxIdentified CONFIGURATION CHANGED: '+params.cfg)
        mfs.setJSON('server.cfg', params.cfg)
        this.publish('mmodNotifications', 'mmNotify', { id: 'configuration changed', message: 'Configuration changed, closing application', icon: 'u.attention', priority: 1, actions: ['form.close'] })
        setTimeout(()=>{
          this.publish('#ALLWITHME', 'mmExit')
        }, 3000)
      }catch(e) {
        this.error('mxIdentified INVALID CONFIGURATION FROM SERVER, IGNORING: ', params.cfg)
      }
    }

    // set channel as identified and inform all applications
    this.ws.app_id = ':'
    this.info('Connected to server: '+this.ws.url+' as '+this.app_id+' '+MOSY_APP_WITH_VERSION)
    this.info('mmAppIdentified online: '+this.app_id, params.unit_id ? ' unit_id: '+params.unit_id : '')
    this.publish('#ALL', 'mmAppIdentified', {
      app_id: this.app_id,
      app_key: this.app_key,
      app_cert: this.app_cert,
      unit_id: params.unit_id,
    })
    mfs.setItem('unit_id', params.unit_id ?? '')

    // inform all listeners we are connected
    this.publish('#ALLWITHME', 'mmServerConnected', { app_id: this.ws.app_id, url: this.ws.url, ident: this?.ws?.ident || '', client_ip: this?.ws?.my_ip_from_server })

    if(this.peering_appid && this.ws && this.ws.app_id) {
      this.publish(':msvrServer', 'mmPeeringConnected', { app_id: this.peering_appid })
    }

    // send offline messages
    this.sendOfflineMessages()
  } // mxIdentified

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * sent by mmodEnroll to msysDomainClient to initiate the enrollment
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.server] - URL for enrollment server
  * @param {string} [params.note] - note for the enrollment
  * @param {string} [params.password] - enrollment password
  */
  async mmAppEnroll(sender, params) {
    params.note = String(params.note??'')
      .replace(/\r?\n.*/, '')
      .replace(/\$HOSTNAME\b/g, mfs.hostname())
      .replace(/\$IP\b/g, _.toString(this?.ws?.my_ip_from_server))

    let app_id
    let app_publickey
    let app_privatekey
    let app_publickey_pem
    if(this.config.identity) {
      const { app_key_password: app_key_pass, app_privatekey: app_key, app_publickey: app_pub } = this.config.identity
      if(mcrypt.verifyKeyPair(app_pub, app_key, app_key_pass)) {
        app_id = this.config.identity.app_id
        app_publickey = mcrypt.publicFromPEM(app_pub)
        app_privatekey = mcrypt.privateFromPEM(app_key, app_key_pass)
        app_publickey_pem = app_pub
      }
    }
    if(!app_publickey || !app_privatekey) {
      app_id = mfs.getItem('app_id')
      app_publickey_pem = mfs.getItem('app_publickey')
      app_publickey = mcrypt.publicFromPEM(app_publickey_pem)
      app_privatekey = mcrypt.privateFromPEM(mfs.getItem('app_privatekey'), this.config.app_key_password)
    }

    if(!app_publickey || !app_privatekey) {
      this.error('mmAppEnroll MISSING app_publickey/app_privatekey')
      const keypair = mcrypt.generateKeyPair() // we don't use async version intentionally, as that requires extra forge script files
      if(!keypair) return this.error('Cannot generate keys')
      app_publickey = keypair.publicKey
      app_privatekey = keypair.privateKey
      this.error('mmAppEnroll CREATING NEW app_publickey')
      mfs.setItem('app_publickey', mcrypt.publicToPEM(app_publickey))
      this.error('mmAppEnroll CREATING NEW app_privatekey')
      mfs.setItem('app_privatekey', mcrypt.privateToPEM(app_privatekey, this.config.app_key_password))
    }

    const host_info = [
      mfs.platform(),
      mfs.os(),
      mfs.device(),
      mfs.hostname(),
      (typeof (navigator)==='object' ? navigator.userAgent : ''),
    ]

    let device_id = ''
    if(this.config.device_identification==='enrollment_note') {
      device_id = params.note
    }else {
      device_id = await mfs.getDeviceId()
    }

    this.enroll = {
      app_id: app_id||'',
      type: MOSY_APP+MOSY_HOST,
      host: host_info.join(':'),
      note: params.note,
      device_id,
      pubkey: app_publickey_pem,
      password: params.password||'',
    }

    const enrollment_server = _.toString(params.server)
    if(enrollment_server) {
      this.config.servers = [params.server]
      const cfg_json = { msysDomainClient: { servers: this.config.servers } }
      this.error('mmAppEnroll CREATING server.cfg: '+JSON.stringify(cfg_json))
      mfs.setJSON('server.cfg', cfg_json)
      this.reconnectServer(false, false) // immediately connect to the server
    }else{
      this.error('mmAppEnroll MISSING enrollment_server')
      this.config.servers = []
      mfs.setItem('server.cfg')
      this.error('mmAppEnroll DELETING app_certificate')
      mfs.setItem('app_certificate')
      this.publish('#ALL', 'mmAppEnrollNeeded')
    }
  } // mmAppEnroll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    this.user_id = params.user_id
    this.app_callsign = params.callsign
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    this.app_status = params.status
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    this.peering_appid = params.app_id
    this.peering_url = params.url
    if(this.peering_appid && this.ws && this.ws.app_id) {
      this.publish(':msvrServer', 'mmPeeringConnected', { app_id: params.app_id })
    }
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} [params.url] - URL of disconnected peering
  */
  mmPeeringDisconnected(sender, params) {
    delete this.peering_appid
    delete this.peering_url
    this.publish(':msvrServer', 'mmPeeringDisconnected')
  } // mmPeeringDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * sent by mmodEnroll to msysDomainClient to abort the enrollment
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmAppEnrollAbort(sender, params) {
    this.warn('mmAppEnrollAbort DELETING app_certificate')
    this.config.servers = []
    mfs.setItem('server.cfg')
    mfs.setItem('app_certificate')
  } // mmAppEnrollAbort

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Listener uses this message to notify #ALL that it successfully finished starting. Normally is not invoked manually, but through this.started() method.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.duration]
  */
  mmStarted(sender, params) {
    if(sender.listener_id === this.listener_id) return
    if(this.ws?.app_id) {
      this.publish(sender, 'mmServerConnected', { app_id: this.ws.app_id, url: this.ws.url, ident: this?.ws?.ident || '', client_ip: this?.ws?.my_ip_from_server })
    }else {
      const url = this.ws?.url ?? ''
      this.publish(sender, 'mmServerDisconnected', { url })
    }
  } // mmStarted
} // msysDomainClient
